import { Node } from "@iventis/domain-model/model/node";
import { assign, createMachine, DoneInvokeEvent, send, SingleOrArray, State, Event, SCXML, EventData, forwardTo, ActorRef, spawn } from "xstate";
import { DataField } from "@iventis/domain-model/model/dataField";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { AxiosResponse } from "axios";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { TwoWayObject } from "@iventis/utilities";
import { PermissionContext, permissionsEditMachine } from "@iventis/permissions/src/permissions-edit.machine";
import { scheduleApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { ScheduleNode } from "./schedule-mock-data";
import { CreateScheduleResponseType } from "./schedule.types";
import { copyScheduleVersionsMachine, CopyVersionsMachineContext, CopyVersionsMachineEvents, COPY_MACHINE_ID, NodeWithExpanded } from "./copy-schedule-versions.machine";
import { UpdatePermissionsIdleStateEvent } from "./create-schedule.machine";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataFieldValue = { dataField: DataField; value: any };

export type CreateSubScheduleContext = {
    schedule: ScheduleVersion;
    parentSchedule: ScheduleNode;
    piblingSchedules: NodeWithExpanded[];
    validationError: { name: string; [dataFieldName: string]: string };
    permissions: PermissionContext;
    copyRef: ActorRef<CopyVersionsMachineEvents, { context: CopyVersionsMachineContext }>;
    isPermissionsIdle: boolean;
    scheduleVersionsCreated: { versions: ScheduleVersion[]; copyPending: boolean };
};

type UpdateNameEvent = {
    type: "UPDATE_NAME";
    payload: string;
};

type UpdateDataFieldEvent = {
    type: "UPDATE_DATA_FIELD";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: { dataFieldId: string; value: any };
};

type UpdatePermissions = { type: "UPDATE_PERMISSIONS"; payload: PermissionContext };

type EventWithNoPayload<T> = {
    type: T;
};

type CopyEvent = EventWithNoPayload<"COPY">;
type NewEvent = EventWithNoPayload<"NEW">;
type CreateEvent = EventWithNoPayload<"CREATE">;
type SaveEvent = EventWithNoPayload<"SAVE">;
type CloseEvent = EventWithNoPayload<"CLOSE">;

export type CreateSubScheduleEvents =
    | UpdateNameEvent
    | UpdateDataFieldEvent
    | CopyEvent
    | NewEvent
    | SaveEvent
    | CreateEvent
    | CloseEvent
    | UpdatePermissions
    | CopyVersionsMachineEvents
    | UpdatePermissionsIdleStateEvent;

export type SendEventToCreateSubSchedule = (
    event: SingleOrArray<Event<CreateSubScheduleEvents>> | SCXML.Event<CreateSubScheduleEvents>,
    payload?: EventData
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => State<CreateSubScheduleContext, CreateSubScheduleEvents, any, any, any>;

/*
    GUARDS
*/

// checks if name is valid
const isNameValid = (name: string) => typeof name === "string" && name.length > 0;
// check if the selected data field value is valid
const isDataFieldValueValid = (dataFieldValue: DataFieldValue) => Boolean(dataFieldValue.value);
// checks if all possible data field values are valid
const areDataFieldValuesValid = (dataFieldValues: DataFieldValue[], dataFieldId: string) =>
    dataFieldValues.filter((dfv) => dfv.dataField.id !== dataFieldId).every((dfv) => Boolean(dfv.value));

const isValid = (context, event) => {
    switch (event.type) {
        case "UPDATE_NAME": {
            return isNameValid(event.payload) && areDataFieldValuesValid(context.schedule.dataFieldValues, null);
        }
        case "UPDATE_DATA_FIELD": {
            return isDataFieldValueValid(event.payload) && isNameValid(context.schedule.name) && areDataFieldValuesValid(context.schedule.dataFieldValues, null);
        }
        default: {
            return areDataFieldValuesValid(context.schedule.dataFieldValues, null) && isNameValid(context.schedule.name);
        }
    }
};

/*
    ACTIONS
*/

const actions = {
    updateName: assign({
        schedule: (context: CreateSubScheduleContext, event: UpdateNameEvent) => ({ ...context.schedule, name: event.payload }),
    }),
    updateDataField: assign({
        schedule: (context: CreateSubScheduleContext, event: UpdateDataFieldEvent) => {
            const { dataFieldValues } = context.schedule;
            const { dataFieldId, value } = event.payload;
            return { ...context.schedule, dataFieldValues: dataFieldValues.map((dfv) => (dfv.dataField.id === dataFieldId ? { ...dfv, value } : dfv)) };
        },
    }),
    updateValidationErrorForName: assign({
        validationError: (context: CreateSubScheduleContext, event: UpdateNameEvent) => ({
            ...context.validationError,
            name: isNameValid(event.payload) ? null : `Invalid name: ${event.payload}`,
        }),
    }),
    updateValidationErrorForDataField: assign({
        validationError: (context: CreateSubScheduleContext, event: UpdateDataFieldEvent) => {
            const { dataFieldValues } = context.schedule;
            const { dataFieldId, value } = event.payload;
            const dataFieldName = dataFieldValues.find((dfv) => dfv.dataField.id === dataFieldId).dataField.name;
            return {
                ...context.validationError,
                [dataFieldName]: isDataFieldValueValid(value) ? null : `Invalid ${dataFieldName}: ${value}`,
            };
        },
    }),
};

/*
    HELPERS
*/

const schedulesToCopyIsNonEmpty = (context: CreateSubScheduleContext) => context.copyRef.getSnapshot().context.scheduleVersionIdsToCopy.length > 0;

/*
    MACHINE
*/

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createSubScheduleMachine = createMachine<CreateSubScheduleContext, CreateSubScheduleEvents>(
    {
        id: "subSchedule",
        initial: "createStep",
        context: {
            schedule: { id: null, type: null, name: "", status: null, dataFieldValues: [], parentId: null, scheduleId: null, publishedAt: null },
            parentSchedule: null,
            piblingSchedules: [],
            validationError: null,
            copyRef: null,
            permissions: {
                originalPermissions: null,
                latestPermissions: null,
                localToRemoteMapping: new TwoWayObject<string, string>({}, {}),
                permissionSelector: null,
                errorMessage: null,
                node: null,
                close: false,
                canLock: false,
                isChild: true,
                isNodeCreated: false,
            },
            isPermissionsIdle: false,
            scheduleVersionsCreated: { versions: [], copyPending: false }, // The Schedule Versions Created
        },
        states: {
            createStep: {
                id: "create",
                initial: "new",
                states: {
                    new: {
                        id: "new",
                        initial: "idle",
                        states: {
                            idle: {
                                initial: "invalid",
                                on: {
                                    UPDATE_NAME: [
                                        {
                                            cond: isValid,
                                            target: ".valid",
                                            actions: actions.updateName,
                                        },
                                        {
                                            target: ".invalid",
                                            actions: [actions.updateName, actions.updateValidationErrorForName],
                                        },
                                    ],
                                    UPDATE_DATA_FIELD: [
                                        {
                                            cond: isValid,
                                            target: ".valid",
                                            actions: actions.updateDataField,
                                        },
                                        {
                                            target: ".invalid",
                                            actions: [actions.updateDataField, actions.updateValidationErrorForDataField],
                                        },
                                    ],
                                    COPY: [{ target: "#copy.idle", cond: (context) => Boolean(context.copyRef) }, { target: "#copy" }], // if we have spawned the machine already we move straight to idle
                                },
                                states: {
                                    valid: {
                                        id: "valid",
                                        on: {
                                            CREATE: "#creating",
                                        },
                                    },
                                    invalid: {
                                        id: "invalid",
                                        // Invalid is always the initial state we come to, but if we have fields pre-filled we can transition to valid if it passes "isValid"
                                        always: [{ cond: isValid, target: "valid" }],
                                    },
                                },
                            },
                            creating: {
                                id: "creating",
                                invoke: {
                                    src: "createSubSchedule",
                                    onDone: {
                                        target: "#permissions",
                                        actions: assign({
                                            permissions: (context, event: DoneInvokeEvent<CreateScheduleResponseType>) => ({ ...context.permissions, node: event.data.node }),
                                            schedule: (_, event: DoneInvokeEvent<CreateScheduleResponseType>) => event.data.schedule,
                                            scheduleVersionsCreated: (_, event: DoneInvokeEvent<CreateScheduleResponseType>) => ({
                                                versions: [event.data.schedule],
                                                copyPending: false,
                                            }),
                                        }),
                                    },
                                    onError: { target: "failure" },
                                },
                            },
                            failure: {},
                        },
                    },
                    copy: {
                        id: "copy",
                        initial: "loading",
                        on: {
                            NEW: "#new",
                        },
                        states: {
                            loading: {
                                entry: assign({
                                    copyRef: ({ piblingSchedules }) =>
                                        spawn(copyScheduleVersionsMachine.withContext({ piblingSchedules, scheduleVersionIdsToCopy: [] }), COPY_MACHINE_ID),
                                }),
                                always: "idle",
                            },
                            idle: {
                                on: {
                                    EXPAND_OR_COLLAPSE: {
                                        actions: forwardTo((context) => context.copyRef),
                                    },
                                    SELECT_OR_DESELECT_NODE: {
                                        actions: forwardTo((context) => context.copyRef),
                                    },
                                    CREATE: [{ target: "copyingScheduleVersions", cond: schedulesToCopyIsNonEmpty }],
                                },
                            },
                            copyingScheduleVersions: {
                                invoke: {
                                    src: "copyScheduleVersions",
                                    onDone: {
                                        actions: assign({
                                            scheduleVersionsCreated: (_, event: DoneInvokeEvent<ScheduleVersion[]>) => ({ versions: event.data, copyPending: true }),
                                        }),
                                        target: "#finished",
                                    },
                                    onError: "failure",
                                },
                            },
                            failure: {},
                        },
                    },
                },
            },
            permissionsStep: {
                id: "permissions",
                invoke: {
                    id: "permissions",
                    src: permissionsEditMachine,
                    onDone: "finished",
                    data: (context) => context.permissions,
                },
                on: {
                    UPDATE_PERMISSIONS: {
                        actions: assign({ permissions: (_, event) => event.payload }),
                    },
                    UPDATE_PERMISSIONS_IDLE_STATE: {
                        actions: assign({ isPermissionsIdle: (_, event) => event.payload }),
                    },
                    SAVE: {
                        actions: send({ type: "SAVE" }, { to: "permissions" }),
                    },
                    CLOSE: {
                        actions: send({ type: "CLOSE" }, { to: "permissions" }),
                    },
                },
            },
            finished: {
                id: "finished",
                type: "final",
            },
        },
    },
    {
        services: {
            createSubSchedule: async (context): Promise<CreateScheduleResponseType> => {
                const schedule: Hierarchy<ScheduleVersion> = { item: context.schedule, children: [] };

                const res = await scheduleApi.post<Hierarchy<ScheduleVersion>, AxiosResponse<ScheduleVersion>>("/schedule-versions", schedule, {
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.schedule.schedule) }),
                        message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.schedule.schedule) }),
                    },
                });
                const scheduleResponse = res.data;

                return {
                    schedule: scheduleResponse,
                    node: { id: scheduleResponse.id, type: NodeType.ScheduleVersion, authorisation: Authorisation.Admin, parentId: scheduleResponse.parentId } as Node, // For permissions all we need is type and id
                };
            },
            copyScheduleVersions: async (context): Promise<ScheduleVersion[]> => {
                const res = await scheduleApi.post<string[], AxiosResponse<ScheduleVersion[]>>(
                    `/schedule-versions/copy/${context.parentSchedule.id}`,
                    context.copyRef
                        .getSnapshot()
                        .context.scheduleVersionIdsToCopy.filter((s) => s.parentId != null)
                        .map((s) => s.id),
                    {
                        customErrorMessage: {
                            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.schedule.schedule) }),
                            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.schedule.schedule) }),
                        },
                    }
                );
                return res.data;
            },
        },
    }
);
